import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Fab,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import axios from "../../utils/axios.tsx";
import TallyDialog from "../dialog/TallyDialog.tsx";
import MobileAppBar from "../conversation/MobileAppBar.tsx";
import DesktopLogoConversationPanel from "../conversation/DesktopLogoConversationPanel.tsx";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import MemoryCard from "./MemoryCard.tsx";

interface MemoriesPageProps {
  openDrawer?: boolean;
  setOpenDrawer: React.Dispatch<React.SetStateAction<boolean>>;
}

interface MemoryItem {
  id: string;
  memory: string;
}

interface ConversationPanelAnimationRef {
  start: () => void;
  stop: () => void;
}

const MemoriesPage: React.FC<MemoriesPageProps> = ({
  openDrawer = false,
  setOpenDrawer,
}) => {
  const theme = useTheme();
  const refAnimation = React.useRef<ConversationPanelAnimationRef>(null);
  const animationStopTimeoutRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const isScreenBiggerThanOrEqualToMd = useMediaQuery(
    theme.breakpoints.up("md"),
  );
  const isScreenBiggerThanOrEqualToLg = useMediaQuery(
    theme.breakpoints.up("lg"),
  );

  const handleDrawerOpen = () => {
    setOpenDrawer(true);
  };

  const fetchMemories = useCallback(async () => {
    const response = await axios.get("/v1/memory/");
    setMemories(response.data.objects || []);
  }, []);

  useEffect(() => {
    fetchMemories();
  }, [fetchMemories]);

  useEffect(() => {
    return () => {
      if (animationStopTimeoutRef.current) {
        clearTimeout(animationStopTimeoutRef.current);
      }
    };
  }, []);

  const triggerLogoAnimation = () => {
    refAnimation.current?.start();
    if (animationStopTimeoutRef.current) {
      clearTimeout(animationStopTimeoutRef.current);
    }
    animationStopTimeoutRef.current = setTimeout(() => {
      refAnimation.current?.stop();
      animationStopTimeoutRef.current = null;
    }, 1200);
  };

  const handleCreateMemory = async (memoryText: string) => {
    triggerLogoAnimation();
    await axios.post("/v1/memory/", JSON.stringify({ content: memoryText }));
    setIsCreateDialogOpen(false);
    fetchMemories();
  };

  const handleOpenCreateDialog = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
  };

  return (
    <Box>
      {isScreenBiggerThanOrEqualToLg && (
        <DesktopLogoConversationPanel refAnimation={refAnimation} />
      )}
      <Box
        sx={[
          {
            position: "fixed",
            top: "50%",
            left: "calc(50%)",
            transform: "translate(-50%, -50%)",
            height: "100%",
            width: {
              xs: "100vw",
              md: "min(92vw, 980px)",
              lg: "min(82vw, 1080px)",
              xl: "min(78vw, 1240px)",
            },
            backgroundColor: theme.palette.secondary.main,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            transition: theme.transitions.create(["left", "width"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          },
          isScreenBiggerThanOrEqualToMd &&
            openDrawer && {
              left: `calc(50% + ${SETTINGS.drawerWidth / 2}px)`,
              width: {
                md: `min(calc(92vw - ${SETTINGS.drawerWidth}px), 980px)`,
                lg: `min(calc(82vw - ${SETTINGS.drawerWidth}px), 1080px)`,
                xl: `min(calc(78vw - ${SETTINGS.drawerWidth}px), 1240px)`,
              },
              transition: theme.transitions.create(["left", "width"], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
        ]}
      >
        {!isScreenBiggerThanOrEqualToLg && (
          <MobileAppBar
            openDrawer={openDrawer}
            anonymousSession={false}
            isScreenBiggerThanOrEqualToLg={isScreenBiggerThanOrEqualToLg}
            handleDrawerOpen={handleDrawerOpen}
          />
        )}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
            pt: { xs: `${SETTINGS.topMarginMobile}px`, lg: 4 },
            pb: { xs: 3, sm: 4 },
            scrollbarColor: "var(--tally-brown) var(--tally-white)",
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" fontWeight="bold">
              <Trans context="Memories page title">Memories</Trans>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              <Trans context="Memories page subtitle">
                Add information to Tally's long-term memory so it can reference
                them in future responses.
              </Trans>
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {memories.map((memory) => (
              <Grid key={memory.id} size={{ xs: 12, sm: 6, md: 6 }}>
                <MemoryCard
                  id={memory.id}
                  memory={memory.memory}
                  onMemoryUpdated={fetchMemories}
                  onMemorySaved={triggerLogoAnimation}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
        <Fab
          color="primary"
          aria-label={i18n._(
            msg({
              context:
                "Accessibility label for creating a memory from memories page",
              message: "Create memory",
            }),
          )}
          onClick={handleOpenCreateDialog}
          sx={{
            position: "absolute",
            right: { xs: 16, sm: 24, md: 28 },
            bottom: { xs: 16, sm: 24, md: 28 },
          }}
        >
          <AddIcon />
        </Fab>
      </Box>
      <TallyDialog
        open={isCreateDialogOpen}
        title={i18n._(
          msg({
            context: "Button title enabling the user to create a memory",
            message: "Create a memory",
          }),
        )}
        subtitle={i18n._(
          msg({
            context:
              "Header with explanation what creating a memory does in the application.",
            message:
              "By creating a memory, I will learn the information so I can use it going forward in our conversations",
          }),
        )}
        submitTitle={i18n._(
          msg({
            context: "Button title for creation of a memory",
            message: "Create",
          }),
        )}
        onSubmit={handleCreateMemory}
        initialInputValue=""
        handleDialogClose={handleCloseCreateDialog}
      />
    </Box>
  );
};

export default MemoriesPage;
